import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, MapPin, ChevronRight, Pin, Building2 } from 'lucide-react';
import {
  noticeTypeConfig,
  noticePriorityConfig,
  noticeTypeBanglaLabels,
  noticePriorityBanglaLabels,
  formatNoticeDate,
  type Notice,
} from '@/types/notices';
import { useLanguage } from '@/contexts/LanguageContext';

interface NoticeCardProps {
  notice: Notice;
  index?: number;
  compact?: boolean;
}

export default function NoticeCard({ notice, index = 0, compact = false }: NoticeCardProps) {
  const { lang, tr } = useLanguage();
  const tCfg = noticeTypeConfig[notice.type];
  const pCfg = noticePriorityConfig[notice.priority];

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.06 }}
    >
      <Link
        to={`/notices/${notice.id}`}
        className="glass-card p-5 block card-hover group relative overflow-hidden"
      >
        {/* Priority accent line */}
        <div className={`absolute top-0 left-0 w-1 h-full ${pCfg.dot} rounded-l-xl`} />

        <div className="pl-3">
          {/* Top row: badges */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {/* Type badge */}
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${tCfg.bg} ${tCfg.color} ${tCfg.border} border`}>
              {lang === 'bn' ? noticeTypeBanglaLabels[notice.type] : tCfg.label}
            </span>

            {/* Priority dot */}
            {notice.priority !== 'normal' && (
              <span className={`text-[10px] font-semibold uppercase tracking-wide ${pCfg.color}`}>
                {lang === 'bn' ? noticePriorityBanglaLabels[notice.priority] : pCfg.label}
              </span>
            )}

            {/* Pinned */}
            {notice.is_pinned && (
              <Pin className="w-3 h-3 text-accent-strong rotate-45" />
            )}
          </div>

          {/* Title */}
          <h3 className={`font-display font-semibold ${compact ? 'text-sm' : 'text-base'} mb-2 group-hover:text-primary transition-colors line-clamp-2`}>
            {notice.title}
          </h3>

          {/* Summary */}
          {!compact && notice.summary && (
            <p className="text-sm text-muted-foreground leading-relaxed mb-3 line-clamp-2">
              {notice.summary}
            </p>
          )}

          {/* Meta row */}
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatNoticeDate(notice.starts_at, lang === 'bn' ? 'bn-BD' : 'en-GB')}
            </span>

            {/* Route context */}
            {notice.routes && notice.routes.length > 0 && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-primary" />
                {notice.routes[0].origin} → {notice.routes[0].destination}
                {notice.routes.length > 1 && ` +${notice.routes.length - 1}`}
              </span>
            )}

            {/* Counter context */}
            {notice.counters && notice.counters.length > 0 && (
              <span className="flex items-center gap-1">
                <Building2 className="w-3 h-3 text-amber-400" />
                {notice.counters[0].name}
                {notice.counters.length > 1 && ` +${notice.counters.length - 1}`}
              </span>
            )}

            {/* CTA hint */}
            <span className="ml-auto flex items-center gap-0.5 text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              {tr('View', 'দেখুন')} <ChevronRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
